import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'
import { FigmaCard, FigmaButton } from '../components/FigmaUIElements'
import { Building, Wrench, Shield, Factory, Users, Star } from 'lucide-react'

const Colonies: React.FC = () => {
  const { settings } = useAnimations()

  return (
    <motion.div
      className="min-h-screen p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-8"
          animate={settings.floatingElements ? {
            y: [0, -5, 0],
            transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
          } : {}}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-purple-600 bg-clip-text text-transparent mb-4">
            Colonial Management
          </h1>
          <p className="text-gray-300 text-lg">
            Expand your empire across the galaxy. Build, develop, and manage your colonies.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Star className="w-8 h-8 text-purple-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">New Colony</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Establish a new colony on an unexplored planet.
            </p>
            <FigmaButton variant="primary" className="w-full">
              Colonize Planet
            </FigmaButton>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Building className="w-8 h-8 text-blue-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Infrastructure</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Develop buildings and infrastructure for your colonies.
            </p>
            <FigmaButton variant="secondary" className="w-full">
              Manage Buildings
            </FigmaButton>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Users className="w-8 h-8 text-green-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Population</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Monitor and manage your colonial populations.
            </p>
            <FigmaButton variant="secondary" className="w-full">
              View Demographics
            </FigmaButton>
          </FigmaCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Active Colonies</h3>
            <div className="space-y-4">
              {[
                { name: 'Alpha Centauri Prime', population: '2.4M', status: 'Thriving' },
                { name: 'Beta Colony', population: '850K', status: 'Growing' },
                { name: 'Gamma Outpost', population: '125K', status: 'Developing' }
              ].map((colony, index) => (
                <motion.div
                  key={colony.name}
                  className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div>
                    <h4 className="text-white font-medium">{colony.name}</h4>
                    <p className="text-gray-400 text-sm">Population: {colony.population}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    colony.status === 'Thriving' ? 'bg-green-500/20 text-green-400' :
                    colony.status === 'Growing' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {colony.status}
                  </span>
                </motion.div>
              ))}
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Resource Production</h3>
            <div className="space-y-4">
              {[
                { resource: 'Energy Crystals', amount: '12,450', trend: '+5.2%' },
                { resource: 'Rare Metals', amount: '8,760', trend: '+3.1%' },
                { resource: 'Bio Materials', amount: '15,230', trend: '+7.8%' }
              ].map((resource, index) => (
                <div key={resource.resource} className="flex items-center justify-between">
                  <span className="text-gray-300">{resource.resource}</span>
                  <div className="text-right">
                    <div className="text-white font-semibold">{resource.amount}</div>
                    <div className="text-green-400 text-sm">{resource.trend}</div>
                  </div>
                </div>
              ))}
            </div>
          </FigmaCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Colonies